import { ReactElement } from 'react';

import { InputFailureSvg, InputSuccessSvg } from '@cashier/cashier-assets';
import {
  Directions,
  PaymentFormField,
  ValidationStatuses,
} from '@cashier/cashier-model';
import { useUIConfiguration } from '@cashier/cashier-shared';
import { cn } from '@cashier/cashier-ui';

import { useFormPayloadItem } from '../../../../../hooks';
import { useSecuredFormStateContext } from '../../../contexts';
import { CommonField } from '../common/cashierField';

import defaultStyles from './defaultStyles.module.scss';
import inlineStyles from './inlineStyles.module.scss';

export interface ComponentProps {
  field: PaymentFormField;
  direction: Directions;
}

export const StandardField = ({
  field,
  direction,
}: ComponentProps): ReactElement => {
  const inputLabelPosition = useUIConfiguration('input', 'label');
  const isDefaultLabelPosition = inputLabelPosition === 'default';

  const { validationStatus } = useFormPayloadItem(field.name);
  const { fieldLoaded, getPayloadItem } = useSecuredFormStateContext();

  const { isFocused, isFilled } = getPayloadItem(field.name);

  const { name, label, placeholder } = field;

  const isError = validationStatus === ValidationStatuses.FAILED;

  const inputStyles = isDefaultLabelPosition
    ? cn({
        [defaultStyles.input]: true,
        [defaultStyles.active]: isFocused,
        [defaultStyles.error]: isError,
        [defaultStyles.rtl]: direction === Directions.Rtl,
      })
    : cn({
        [inlineStyles.input]: true,
        [inlineStyles.error]: isError,
        [inlineStyles.rtl]: direction === Directions.Rtl,
      });

  return (
    <div
      className={cn({
        [inlineStyles.wrapper]: true,
        [defaultStyles.wrapper]: true,
        [defaultStyles.col]: true,
      })}
    >
      {isDefaultLabelPosition && (
        <>
          <label
            htmlFor={name}
            className={defaultStyles.inputLabel}
            data-test={`${name}-input-label`}
          >
            {label}
          </label>
          <span
            className={cn({
              [defaultStyles.placeholder]: true,
              [defaultStyles.active]: isFocused || isFilled,
              [defaultStyles.rtl]: direction === Directions.Rtl,
            })}
          >
            {placeholder || ''}
          </span>
        </>
      )}

      <div className={inputStyles}>
        <CommonField
          name={name}
          title={name}
          loading="lazy"
          onLoad={() => fieldLoaded(name)}
          labelPosition={inputLabelPosition}
        />
      </div>

      {!isDefaultLabelPosition && (
        <>
          <label
            className={cn({
              [inlineStyles.label]: true,
              [inlineStyles.active]: isFocused || isFilled,
              [inlineStyles.rtl]: direction === Directions.Rtl,
            })}
          >
            {label}
          </label>

          <div
            className={cn({
              [inlineStyles.icon]: true,
              [inlineStyles.rtl]: direction === Directions.Rtl,
            })}
          >
            {validationStatus === ValidationStatuses.SUCCESS && (
              <div className={inlineStyles.success}>
                <InputSuccessSvg />
              </div>
            )}
            {isError && (
              <div className={inlineStyles.error}>
                <InputFailureSvg />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
